'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Handle, Position, NodeProps } from 'reactflow';
import {
  Users,
  Zap,
  Pencil,
  Shuffle,
  Trash2,
  Eye,
  Check,
  X,
  AlertTriangle,
  MoreHorizontal,
  Palette,
  ArrowRight,
  Download,
  Link as LinkIcon,
  Copy,
  RefreshCw,
  Monitor,
  Smartphone,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { screenshotKey } from '../hooks/useInstanceScreenshotsForFunnel';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { FlowStep } from '../types';
import { cn } from '@/lib/utils';
import { STEP_ICONS, STEP_BG_COLORS, STEP_TYPE_LABELS, STEP_BORDER_COLORS } from '../constants/stepStyles';
import { HANDLE_IDS } from '../constants/handles';
import { getIntegrationMetadata } from '@/lib/integrations-constants';
import { triggerAttention } from '@/hooks/use-attention-guide';

interface FlowStepNodeData {
  step: FlowStep;
  isSelected: boolean;
  onSelect: () => void;
  onEdit?: () => void;
  onEditConfig?: () => void;
  onTransformToSplit?: () => void;
  onDuplicateDesign?: () => void;
  onDelete?: () => void;
  onPreview?: () => void;
  onRetryScreenshot?: (variantId?: string) => void;
  onGetCheckoutLink?: () => void;
  onLabelChange?: (newLabel: string) => void;
  autoEdit?: boolean;
  showPreviews?: boolean;
  isWaitingForScreenshot?: boolean;
  isFirstStep?: boolean;
  forceShowActionBar?: boolean;
  isMissingDesign?: boolean;
  integrationUrls?: Record<string, string>;
  instanceScreenshots?: Record<string, string>;
  connectedProviders?: string[];
  stepAnalytics?: {
    visitors: number;
    conversions: number;
    progressionRate: number | null;
    progressedToNext: number;
    realtimeVisitors: number;
    conversionValue: number;
    conversionValueUnit: string;
    avgConversionValue: number;
    hasNextStep: boolean;
  };
  variantAnalytics?: Record<
    string,
    {
      visitors: number;
      conversions: number;
      progressionRate: number | null;
      progressedToNext: number;
      realtimeVisitors: number;
      conversionValue: number;
      conversionValueUnit: string;
      avgConversionValue: number;
      hasNextStep: boolean;
    }
  >;
}

function formatNumber(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return n.toLocaleString();
}

function formatCurrency(value: number, unit: string): string {
  if (value >= 1000) return `${(value / 1000).toFixed(1)}k ${unit}`;
  if (value > 0) return `${value.toFixed(0)} ${unit}`;
  return '';
}

export function FlowStepNode({ data, selected }: NodeProps<FlowStepNodeData>) {
  const { t } = useTranslation('storefront');
  const {
    step,
    onSelect,
    onEdit,
    onEditConfig,
    onTransformToSplit,
    onDuplicateDesign,
    onDelete,
    onPreview,
    onRetryScreenshot,
    onGetCheckoutLink,
    onLabelChange,
    autoEdit,
    stepAnalytics,
    variantAnalytics,
    isWaitingForScreenshot,
    isFirstStep,
    forceShowActionBar,
    isMissingDesign,
    integrationUrls,
    instanceScreenshots,
    connectedProviders,
  } = data;

  const Icon = STEP_ICONS[step.type];
  const isSplit = !!step.variants && step.variants.length > 0;

  const integrationLogo = React.useMemo(() => {
    if (step.type === 'storefront_shopify') return getIntegrationMetadata('shopify')?.logoUrl;
    if (step.type === 'storefront_woocommerce') return getIntegrationMetadata('woocommerce')?.logoUrl;
    return null;
  }, [step.type]);

  const [selectedVariantIndex, setSelectedVariantIndex] = React.useState(0);
  // Device toggle for the preview thumbnail: the capture job stores both a
  // desktop and a mobile shot (url / mobileUrl) on every run — this only
  // selects which one to display. Local state so it never touches the
  // canvas-level node cache.
  const [previewDevice, setPreviewDevice] = React.useState<'desktop' | 'mobile'>('desktop');

  const screenshot = React.useMemo(() => {
    if (isSplit && step.variants && step.variants.length > 0) {
      const variantId = step.variants[selectedVariantIndex]?.id;
      const variantScreenshot = variantId ? (step as any).metadata?.variantScreenshots?.[variantId] : null;
      if (variantScreenshot?.url) return variantScreenshot;
      const variantInstanceId = step.variants[selectedVariantIndex]?.config?.instanceId;
      const variantPath = step.variants[selectedVariantIndex]?.config?.metadata?.internalPath
        ?? step.variants[selectedVariantIndex]?.config?.pagePath
        ?? step.variants[selectedVariantIndex]?.config?.path;
      const deploymentUrl = variantInstanceId && (
        instanceScreenshots?.[screenshotKey(variantInstanceId, variantPath)] ||
        instanceScreenshots?.[variantInstanceId]
      );
      if (deploymentUrl) return { url: deploymentUrl, capturedAt: '', isFromDeployment: true };
      return null;
    }
    const previewScreenshot = (step as any).metadata?.previewScreenshot;
    if (previewScreenshot?.url) return previewScreenshot;
    const instanceId = step.config?.instanceId;
    const pagePath = step.config?.metadata?.internalPath ?? step.config?.pagePath ?? step.config?.path;
    const deploymentUrl = instanceId && (
      instanceScreenshots?.[screenshotKey(instanceId, pagePath)] ||
      instanceScreenshots?.[instanceId]
    );
    if (deploymentUrl) return { url: deploymentUrl, capturedAt: '', isFromDeployment: true };
    return null;
  }, [isSplit, step.variants, step.metadata, step.config?.instanceId, step.config?.metadata?.internalPath, step.config?.pagePath, step.config?.path, selectedVariantIndex, instanceScreenshots]);

  const hasAnyScreenshot = React.useMemo(() => {
    if (isWaitingForScreenshot) return true;
    if (isSplit && step.variants && step.variants.length > 0) {
      return step.variants.some((variant) => {
        const variantScreenshot = (step as any).metadata?.variantScreenshots?.[variant.id];
        if (variantScreenshot?.url) return true;
        const vId = variant.config?.instanceId;
        const vPath = variant.config?.metadata?.internalPath ?? variant.config?.pagePath ?? variant.config?.path;
        return !!(vId && (instanceScreenshots?.[screenshotKey(vId, vPath)] || instanceScreenshots?.[vId]));
      });
    }
    const previewScreenshot = (step as any).metadata?.previewScreenshot;
    if (previewScreenshot?.url) return true;
    const instanceId = step.config?.instanceId;
    const pagePath = step.config?.metadata?.internalPath ?? step.config?.pagePath ?? step.config?.path;
    return !!(instanceId && (instanceScreenshots?.[screenshotKey(instanceId, pagePath)] || instanceScreenshots?.[instanceId]));
  }, [isSplit, step.variants, step.metadata, step.config?.instanceId, step.config?.metadata?.internalPath, step.config?.pagePath, step.config?.path, isWaitingForScreenshot, instanceScreenshots]);

  // Failure marker written by the screenshot-capture inngest job when a
  // capture errored (vs. simply never having run). Resolved per selected
  // variant for split steps, mirroring the `screenshot` memo above. Drives
  // the "preview failed / Retry" empty state below.
  const captureError = React.useMemo<{ message?: string; failedAt?: string } | null>(() => {
    if (isSplit && step.variants && step.variants.length > 0) {
      const variantId = step.variants[selectedVariantIndex]?.id;
      return variantId ? ((step as any).metadata?.variantScreenshotErrors?.[variantId] ?? null) : null;
    }
    return (step as any).metadata?.previewScreenshotError ?? null;
  }, [isSplit, step.variants, step.metadata, selectedVariantIndex]);

  // URL actually displayed: honor the device toggle when a mobile shot
  // exists; deployment-fallback (P1) images only have a single URL.
  const displayedScreenshotUrl = React.useMemo(() => {
    if (!screenshot?.url) return undefined;
    const mobileUrl = (screenshot as any)?.mobileUrl;
    return previewDevice === 'mobile' && mobileUrl ? mobileUrl : screenshot.url;
  }, [screenshot, previewDevice]);

  // A newer design was saved than the displayed capture (designSavedAt is
  // stamped by the Studio save-back; the recapture overwrites the shot when
  // it lands). Surface it as an "updating preview" overlay on the OLD image
  // instead of leaving merchants wondering why the thumbnail lags their
  // edit. Cleared by the fresh capture (capturedAt catches up) or by a
  // capture-error marker newer than the save (stop implying progress).
  const isRecapturePending = React.useMemo(() => {
    const meta: any = step.metadata || {};
    const shot: any = screenshot;
    if (!shot?.capturedAt || shot.isFromDeployment || shot.isPlaceholder) return false;
    const savedAt: number | undefined = meta.designSavedAt;
    if (!savedAt) return false;
    const capturedMs = Date.parse(shot.capturedAt);
    if (Number.isNaN(capturedMs) || savedAt <= capturedMs) return false;
    const selectedVariantId = step.variants?.[selectedVariantIndex]?.id;
    const errFailedAt = isSplit && selectedVariantId
      ? meta.variantScreenshotErrors?.[selectedVariantId]?.failedAt
      : meta.previewScreenshotError?.failedAt;
    if (errFailedAt && Date.parse(errFailedAt) > savedAt) return false;
    return true;
  }, [screenshot, step.metadata, isSplit, step.variants, selectedVariantIndex]);

  const handleRetryCapture = (e: React.MouseEvent) => {
    e.stopPropagation();
    const variantId = isSplit ? step.variants?.[selectedVariantIndex]?.id : undefined;
    onRetryScreenshot?.(variantId);
  };

  const [isEditingLabel, setIsEditingLabel] = React.useState(false);
  const [editedLabel, setEditedLabel] = React.useState(step.label);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const hasConfigToEdit =
    step.type.startsWith('storefront_')
      ? true
      : isSplit
        ? step.variants && step.variants.length > 0 && (step.variants[0].config?.instanceId || step.variants[0].config?.routeId)
        : (step.config?.instanceId || step.config?.routeId);

  const handleLabelSave = () => {
    const trimmedLabel = editedLabel.trim();
    if (trimmedLabel && trimmedLabel !== step.label && onLabelChange) {
      onLabelChange(trimmedLabel);
    }
    setIsEditingLabel(false);
  };

  const handleLabelCancel = () => {
    setEditedLabel(step.label);
    setIsEditingLabel(false);
  };

  React.useEffect(() => {
    if (autoEdit && !isEditingLabel) {
      setIsEditingLabel(true);
    }
  }, [autoEdit, isEditingLabel, step.id]);

  React.useEffect(() => {
    if (isEditingLabel) {
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    }
  }, [isEditingLabel]);

  React.useEffect(() => {
    setEditedLabel(step.label);
  }, [step.label]);

  // Aggregate analytics for display
  const metrics = stepAnalytics || {
    visitors: 0,
    conversions: 0,
    progressionRate: null,
    progressedToNext: 0,
    realtimeVisitors: 0,
    conversionValue: 0,
    conversionValueUnit: 'USD',
    avgConversionValue: 0,
    hasNextStep: false,
  };
  const hasAnalytics = metrics.visitors > 0 || metrics.realtimeVisitors > 0;

  // The offer-step data-tour attr drives the offer-reminder tour. Two node
  // shapes qualify: an explicit offer step, or a BOGO checkout where offers
  // are configured inline inside the checkout step's stepConfig.checkoutOffers.
  const isBogoCheckout =
    step.type === 'checkout' && (step.config?.pluginId || '').startsWith('bogo-checkout');
  const needsOfferSelection = step.type === 'offer' || isBogoCheckout;
  // For BOGO funnels the entry node is BOTH the first step AND the offer
  // target. We resolve the conflict via design state: when no design is set
  // the editor-setup tour still needs the first-step anchor; once designs
  // exist the editor tour has self-suppressed and the offer tour should win.
  const hasDesignVariant =
    !!step.config?.variant || !!step.variants?.some((v: any) => v.config?.variant);
  const tourAttr =
    needsOfferSelection && hasDesignVariant
      ? { 'data-tour': 'offer-step' }
      : isFirstStep
        ? { 'data-tour': 'first-step' }
        : needsOfferSelection
          ? { 'data-tour': 'offer-step' }
          : {};

  return (
    <div className="group/node relative" {...tourAttr}>
      {/* Input Handle (left) */}
      <Handle
        type="target"
        position={Position.Left}
        id={HANDLE_IDS.LEFT}
        className={cn(
          '!h-5 !w-5 !cursor-crosshair !rounded-full !border-2 !border-white !shadow-md !transition-all !duration-200',
          'hover:!h-6 hover:!w-6 hover:!shadow-lg',
          selected
            ? '!bg-primary hover:!ring-4 hover:!ring-primary/40'
            : '!bg-slate-400 hover:!bg-slate-500 hover:!ring-4 hover:!ring-slate-300',
        )}
        style={{ left: '-10px' }}
        isConnectable={true}
      />

      <Card
        className={cn(
          'w-[300px] cursor-pointer overflow-hidden border-2 p-0 transition-all',
          selected
            ? 'border-primary shadow-lg ring-2 ring-primary/30 dark:ring-primary/30'
            : isMissingDesign
              ? 'border-amber-400 ring-2 ring-amber-200 dark:border-amber-500 dark:ring-amber-800/50'
              : 'border-slate-200 hover:border-primary/60 dark:border-slate-700',
          isSplit && 'border-l-4 border-l-purple-500',
        )}
        onClick={onSelect}
      >
        <div className="flex flex-col">
          {/* ── Header: icon + label + status dots ─────────────────── */}
          <div className="flex items-center gap-2 bg-white px-2.5 py-1.5 dark:bg-slate-950">
            <div
              className={cn(
                'flex h-6 w-6 flex-shrink-0 items-center justify-center rounded',
                integrationLogo
                  ? `border-2 bg-white ${STEP_BORDER_COLORS[step.type]}`
                  : `text-white ${STEP_BG_COLORS[step.type]}`,
              )}
            >
              {integrationLogo ? (
                <img src={integrationLogo} alt={step.type} className="h-3.5 w-3.5 object-contain" />
              ) : (
                <Icon className="h-3 w-3" />
              )}
            </div>

            <div className="min-w-0 flex-1">
              {isEditingLabel ? (
                <div className="nodrag nopan flex items-center gap-1">
                  <Input
                    ref={inputRef}
                    value={editedLabel}
                    onChange={(e) => setEditedLabel(e.target.value)}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === 'Enter') handleLabelSave();
                      else if (e.key === 'Escape') handleLabelCancel();
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="h-6 flex-1 text-xs font-semibold"
                    placeholder={t('flowStep.stepNamePlaceholder')}
                  />
                  <button onClick={(e) => { e.stopPropagation(); handleLabelSave(); }} className="flex h-5 w-5 items-center justify-center rounded bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-300" title={t('flowStep.save')}>
                    <Check className="h-3 w-3" />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); handleLabelCancel(); }} className="flex h-5 w-5 items-center justify-center rounded bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300" title={t('flowStep.cancel')}>
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={(e) => { e.stopPropagation(); if (onLabelChange) setIsEditingLabel(true); }}
                    className={cn(
                      'nodrag nopan truncate text-left text-xs font-semibold dark:text-slate-100',
                      onLabelChange && 'cursor-text rounded px-0.5 transition-colors hover:text-primary dark:hover:text-primary',
                    )}
                    title={onLabelChange ? t('flowStep.clickToEdit') : step.label}
                  >
                    {step.label}
                  </button>
                  {/* Status dots with tooltips */}
                  <TooltipProvider delayDuration={200}>
                    <div className="flex items-center gap-0.5">
                      {(step.conditions.entry || step.isDefault) && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="h-2 w-2 rounded-full bg-green-500" />
                          </TooltipTrigger>
                          <TooltipContent side="top" className="text-[10px]">{t('flowStep.entryPoint')}</TooltipContent>
                        </Tooltip>
                      )}
                      {isSplit && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="h-2 w-2 rounded-full bg-purple-500" />
                          </TooltipTrigger>
                          <TooltipContent side="top" className="text-[10px]">{t('flowStep.abTestVariants', { count: step.variants?.length ?? 0 })}</TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </TooltipProvider>
                </div>
              )}
            </div>

            {/* Type badge - far right, subtle */}
            <span className="flex-shrink-0 text-[9px] font-medium uppercase tracking-wider text-slate-400 dark:text-slate-500">
              {STEP_TYPE_LABELS[step.type]}
            </span>
          </div>

          {/* ── Preview / Empty state ───────────────────────────── */}
          {data.showPreviews !== false && (
            <div className="border-t border-slate-100 dark:border-slate-800">
              <div className="relative overflow-hidden bg-slate-50 dark:bg-slate-800/50">
                {isWaitingForScreenshot || isRecapturePending ? (
                  // h-52 on the wrapper so the spinner keeps full height even
                  // when there is no existing screenshot to size it — otherwise
                  // the absolute overlay has nothing to fill and the block
                  // collapses to a bare header.
                  <div className="relative h-52">
                    {screenshot && !screenshot.isPlaceholder && (
                      <img src={displayedScreenshotUrl} alt={t('flowStep.stepPreviewAlt')} className="h-52 w-full object-cover object-top opacity-50" />
                    )}
                    {/* Keep the device toggle usable above the updating
                        overlay — the old capture is still being displayed. */}
                    {screenshot && !screenshot.isPlaceholder && (screenshot as any).mobileUrl && (
                      <div
                        className="absolute bottom-1 right-1 z-10 flex overflow-hidden rounded border border-slate-200 bg-white/95 shadow-sm dark:border-slate-700 dark:bg-slate-900/95"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          title={t('flowStep.previewDesktop')}
                          onClick={() => setPreviewDevice('desktop')}
                          className={cn(
                            'flex h-4 w-5 items-center justify-center transition-colors',
                            previewDevice === 'desktop'
                              ? 'bg-primary text-white'
                              : 'text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300',
                          )}
                        >
                          <Monitor className="h-2.5 w-2.5" />
                        </button>
                        <button
                          type="button"
                          title={t('flowStep.previewMobile')}
                          onClick={() => setPreviewDevice('mobile')}
                          className={cn(
                            'flex h-4 w-5 items-center justify-center transition-colors',
                            previewDevice === 'mobile'
                              ? 'bg-primary text-white'
                              : 'text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300',
                          )}
                        >
                          <Smartphone className="h-2.5 w-2.5" />
                        </button>
                      </div>
                    )}
                    <div className="absolute inset-0 flex h-52 w-full items-center justify-center bg-slate-100/80 dark:bg-slate-900/80">
                      <div className="px-3 text-center">
                        <div className="relative mx-auto mb-2 h-7 w-7">
                          <div className="absolute inset-0 animate-spin rounded-full border-2 border-slate-300 border-t-primary dark:border-slate-600 dark:border-t-primary" />
                        </div>
                        <p className="text-[10px] font-medium text-slate-500 dark:text-slate-400">
                          {isRecapturePending && !isWaitingForScreenshot
                            ? t('flowStep.updatingPreview')
                            : t('flowStep.capturing')}
                        </p>
                        <p className="mx-auto mt-0.5 max-w-[160px] text-[9px] leading-tight text-slate-400 dark:text-slate-500">
                          {isRecapturePending && !isWaitingForScreenshot
                            ? t('flowStep.updatingPreviewHint')
                            : t('flowStep.capturingHint')}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : screenshot && !screenshot.isPlaceholder ? (
                  <>
                    <img src={displayedScreenshotUrl} alt={t('flowStep.stepPreviewAlt')} className="h-52 w-full object-cover object-top" />
                    <div className="absolute bottom-1 right-1 flex items-center gap-1">
                      {(screenshot as any).mobileUrl && (
                        <div
                          className="flex overflow-hidden rounded border border-slate-200 bg-white/95 shadow-sm dark:border-slate-700 dark:bg-slate-900/95"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            title={t('flowStep.previewDesktop')}
                            onClick={() => setPreviewDevice('desktop')}
                            className={cn(
                              'flex h-4 w-5 items-center justify-center transition-colors',
                              previewDevice === 'desktop'
                                ? 'bg-primary text-white'
                                : 'text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300',
                            )}
                          >
                            <Monitor className="h-2.5 w-2.5" />
                          </button>
                          <button
                            type="button"
                            title={t('flowStep.previewMobile')}
                            onClick={() => setPreviewDevice('mobile')}
                            className={cn(
                              'flex h-4 w-5 items-center justify-center transition-colors',
                              previewDevice === 'mobile'
                                ? 'bg-primary text-white'
                                : 'text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300',
                            )}
                          >
                            <Smartphone className="h-2.5 w-2.5" />
                          </button>
                        </div>
                      )}
                      {(screenshot as any).isFromDeployment ? (
                        <Badge variant="secondary" className="h-4 px-1 text-[9px] shadow-sm">{t('flowStep.design')}</Badge>
                      ) : (
                        <Badge variant="secondary" className="h-4 px-1 text-[9px] shadow-sm">
                          {(() => {
                            const d = new Date(screenshot.capturedAt);
                            const mins = Math.floor((Date.now() - d.getTime()) / 60000);
                            if (Number.isNaN(mins) || mins < 0) return '';
                            if (mins < 1) return 'now';
                            if (mins < 60) return `${mins}m`;
                            const hrs = Math.floor(mins / 60);
                            if (hrs < 24) return `${hrs}h`;
                            return `${Math.floor(hrs / 24)}d`;
                          })()}
                        </Badge>
                      )}
                    </div>
                  </>
                ) : isMissingDesign ? (
                  <div
                    onClick={(e) => { e.stopPropagation(); onEdit?.(); }}
                    className={cn(
                      'flex h-44 w-full cursor-pointer flex-col items-center justify-center gap-2 bg-gradient-to-b from-slate-50 to-slate-100 transition-colors hover:from-primary/5 hover:to-primary/10 dark:from-slate-900 dark:to-slate-800 dark:hover:from-primary/10 dark:hover:to-primary/5',
                    )}
                  >
                    <div className={cn('flex h-10 w-10 items-center justify-center rounded-xl text-white', STEP_BG_COLORS[step.type])}>
                      <Palette className="h-5 w-5" />
                    </div>
                    <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">{t('flowStep.chooseDesign')}</span>
                    <span className="text-[10px] text-slate-400 dark:text-slate-500">{t('flowStep.clickToSelectTemplate')}</span>
                  </div>
                ) : captureError || screenshot?.isPlaceholder ? (
                  // Preview couldn't be generated (capture timed out/failed, or a
                  // placeholder was written as a stand-in). Show a neutral empty
                  // state with a retry affordance instead of the placeholder image.
                  <div className="flex h-44 w-full flex-col items-center justify-center gap-2 bg-gradient-to-b from-slate-50 to-slate-100 px-4 dark:from-slate-900 dark:to-slate-800">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-200/70 text-slate-400 dark:bg-slate-800 dark:text-slate-500">
                      <Eye className="h-5 w-5" />
                    </div>
                    <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">{t('flowStep.noPreviewAvailable')}</span>
                    {onRetryScreenshot && (
                      <button
                        type="button"
                        onClick={handleRetryCapture}
                        className="mt-0.5 inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[10px] font-medium text-slate-600 shadow-sm transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                      >
                        <RefreshCw className="h-3 w-3" />
                        {t('flowStep.retryCapture')}
                      </button>
                    )}
                  </div>
                ) : hasAnyScreenshot ? (
                  // Has screenshot somewhere but not for current variant
                  <div className="flex h-44 w-full items-center justify-center bg-slate-100 dark:bg-slate-900/50">
                    <div className="text-center">
                      <Eye className="mx-auto mb-1 h-6 w-6 text-slate-300 dark:text-slate-600" />
                      <p className="text-[10px] text-slate-400 dark:text-slate-500">{t('flowStep.noPreview')}</p>
                    </div>
                  </div>
                ) : step.type === 'storefront_shopify' && step.config?.url ? (
                  <div
                    onClick={(e) => { e.stopPropagation(); onEdit?.(); }}
                    className="flex h-36 w-full cursor-pointer flex-col items-center justify-center gap-2.5 bg-gradient-to-b from-emerald-50/60 to-slate-50 transition-colors hover:from-emerald-50 hover:to-emerald-100/50 dark:from-emerald-950/20 dark:to-slate-900 dark:hover:from-emerald-950/40 dark:hover:to-slate-800"
                  >
                    {integrationLogo && (
                      <div className="relative flex h-10 w-10 items-center justify-center rounded-xl border-2 border-emerald-200 bg-white dark:border-emerald-800 dark:bg-slate-800">
                        <img src={integrationLogo} alt="Shopify" className="h-5 w-5 object-contain" />
                        <div className="absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-white ring-2 ring-white dark:ring-slate-900">
                          <Check className="h-2.5 w-2.5" />
                        </div>
                      </div>
                    )}
                    <div className="text-center">
                      <div className="flex items-center gap-1 text-xs font-semibold text-emerald-700 dark:text-emerald-400">
                        <Check className="h-3 w-3" />
                        {t('flowStep.scriptInstalled')}
                      </div>
                      <p className="mt-0.5 max-w-[200px] truncate text-[10px] text-slate-400 dark:text-slate-500">
                        {step.config.url.replace(/^https?:\/\//, '')}
                      </p>
                    </div>
                  </div>
                ) : step.type === 'storefront_shopify' ? (
                  <div
                    onClick={(e) => { e.stopPropagation(); onEdit?.(); triggerAttention('shopify-install'); }}
                    className="flex h-36 w-full cursor-pointer flex-col items-center justify-center gap-2 bg-gradient-to-b from-slate-50 to-slate-100 transition-colors hover:from-amber-50/80 hover:to-amber-100/50 dark:from-slate-900 dark:to-slate-800 dark:hover:from-amber-950/30 dark:hover:to-amber-900/20"
                  >
                    {integrationLogo && (
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl border-2 border-amber-200 bg-white dark:border-amber-800 dark:bg-slate-800">
                        <img src={integrationLogo} alt="Shopify" className="h-5 w-5 object-contain" />
                      </div>
                    )}
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300">
                      <Download className="h-3.5 w-3.5" />
                      {t('flowStep.installScript')}
                    </div>
                    <span className="text-[10px] text-slate-400 dark:text-slate-500">{t('flowStep.clickToConfigure')}</span>
                  </div>
                ) : step.type === 'storefront_woocommerce' && (integrationUrls?.['woocommerce'] || connectedProviders?.includes('woocommerce') || step.metadata?.woocommerceConfigured) ? (
                  <div
                    onClick={(e) => { e.stopPropagation(); onEdit?.(); }}
                    className="flex h-36 w-full cursor-pointer flex-col items-center justify-center gap-2.5 bg-gradient-to-b from-emerald-50/60 to-slate-50 transition-colors hover:from-emerald-50 hover:to-emerald-100/50 dark:from-emerald-950/20 dark:to-slate-900 dark:hover:from-emerald-950/40 dark:hover:to-slate-800"
                  >
                    {integrationLogo && (
                      <div className="relative flex h-10 w-10 items-center justify-center rounded-xl border-2 border-emerald-200 bg-white dark:border-emerald-800 dark:bg-slate-800">
                        <img src={integrationLogo} alt="WooCommerce" className="h-5 w-5 object-contain" />
                        <div className="absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-white ring-2 ring-white dark:ring-slate-900">
                          <Check className="h-2.5 w-2.5" />
                        </div>
                      </div>
                    )}
                    <div className="text-center">
                      <div className="flex items-center gap-1 text-xs font-semibold text-emerald-700 dark:text-emerald-400">
                        <Check className="h-3 w-3" />
                        {t('flowStep.pluginInstalled')}
                      </div>
                      {integrationUrls?.['woocommerce'] && (
                        <p className="mt-0.5 max-w-[200px] truncate text-[10px] text-slate-400 dark:text-slate-500">
                          {integrationUrls['woocommerce'].replace(/^https?:\/\//, '')}
                        </p>
                      )}
                    </div>
                  </div>
                ) : step.type === 'storefront_woocommerce' ? (
                  <div
                    onClick={(e) => { e.stopPropagation(); onEdit?.(); }}
                    className="flex h-36 w-full cursor-pointer flex-col items-center justify-center gap-2 bg-gradient-to-b from-slate-50 to-slate-100 transition-colors hover:from-amber-50/80 hover:to-amber-100/50 dark:from-slate-900 dark:to-slate-800 dark:hover:from-amber-950/30 dark:hover:to-amber-900/20"
                  >
                    {integrationLogo && (
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl border-2 border-amber-200 bg-white dark:border-amber-800 dark:bg-slate-800">
                        <img src={integrationLogo} alt="WooCommerce" className="h-5 w-5 object-contain" />
                      </div>
                    )}
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300">
                      <Download className="h-3.5 w-3.5" />
                      {t('flowStep.installPlugin')}
                    </div>
                    <span className="text-[10px] text-slate-400 dark:text-slate-500">{t('flowStep.clickToConfigure')}</span>
                  </div>
                ) : (
                  <div className="flex h-32 w-full items-center justify-center bg-slate-50 dark:bg-slate-900/50">
                    <span className="text-[10px] text-slate-400 dark:text-slate-500">{t('flowStep.noPreviewAvailable')}</span>
                  </div>
                )}

                {/* Variant switcher overlay */}
                {isSplit && step.variants && step.variants.length > 1 && (
                  <div className="nodrag nopan absolute left-1 top-1 flex gap-0.5">
                    {step.variants.map((variant, idx) => {
                      const variantScreenshot = (step as any).metadata?.variantScreenshots?.[variant.id];
                      // Mirror the screenshot memo's lookup: deployment
                      // screenshots are keyed by instanceId+pagePath first —
                      // checking only the bare instanceId showed "No preview"
                      // styling for variants that DO have one (FE-17).
                      const variantPath = variant.config?.metadata?.internalPath
                        ?? variant.config?.pagePath
                        ?? variant.config?.path;
                      const hasScreenshot =
                        (variantScreenshot && variantScreenshot.url) ||
                        !!(variant.config?.instanceId && (
                          instanceScreenshots?.[screenshotKey(variant.config.instanceId, variantPath)] ||
                          instanceScreenshots?.[variant.config.instanceId]
                        ));
                      return (
                        <button
                          key={variant.id}
                          onClick={(e) => { e.stopPropagation(); setSelectedVariantIndex(idx); }}
                          className={cn(
                            'flex h-5 w-5 items-center justify-center rounded text-[10px] font-bold transition-all',
                            selectedVariantIndex === idx
                              ? 'bg-purple-600 text-white shadow-md ring-2 ring-white'
                              : hasScreenshot
                                ? 'bg-white/90 text-slate-600 hover:bg-purple-100 hover:text-purple-700'
                                : 'bg-slate-300/90 text-slate-400 hover:bg-slate-400/90',
                          )}
                          title={`${variant.label}${step.splitType === 'geo'
                            ? variant.regions?.length ? ` (${variant.regions.join(', ')})` : ' (No regions)'
                            : ` (${variant.percentage}%)`
                          }${!hasScreenshot ? ' - No preview' : ''}`}
                        >
                          {String.fromCharCode(65 + idx)}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* "No Design" alert overlay at bottom */}
                {isMissingDesign && screenshot && (
                  <div className="absolute bottom-1 left-1">
                    <Badge className="h-4 gap-0.5 bg-amber-500 px-1 text-[9px] font-semibold text-white shadow-sm">
                      <AlertTriangle className="h-2.5 w-2.5" />
                      {t('flowStep.noDesign')}
                    </Badge>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Analytics (always visible) ─────────────────────── */}
          {!isSplit && (
            <div className={cn(
              'border-t border-slate-100 dark:border-slate-800',
              hasAnalytics ? 'bg-slate-50/50 dark:bg-slate-900/50' : 'bg-slate-50/30 dark:bg-slate-900/30',
            )}>
              <div className="flex items-center gap-1.5 px-2.5 py-1.5">
                {metrics.realtimeVisitors > 0 && (
                  <div className="flex items-center gap-0.5" title={t('flowStep.activeNow')}>
                    <div className="relative flex h-3.5 w-3.5 items-center justify-center">
                      <div className="absolute h-2 w-2 animate-ping rounded-full bg-green-400 opacity-75" />
                      <Zap className="relative h-2.5 w-2.5 text-green-600 dark:text-green-400" />
                    </div>
                    <span className="text-[9px] font-bold tabular-nums text-green-700 dark:text-green-300">{metrics.realtimeVisitors}</span>
                    <div className="mx-0.5 h-2.5 w-px bg-slate-200 dark:bg-slate-700" />
                  </div>
                )}

                <div className="flex items-center gap-0.5" title={`${metrics.visitors.toLocaleString()} visitors`}>
                  <Users className={cn('h-2.5 w-2.5', hasAnalytics ? 'text-slate-400 dark:text-slate-500' : 'text-slate-300 dark:text-slate-600')} />
                  <span className={cn(
                    'text-[9px] tabular-nums',
                    hasAnalytics ? 'font-semibold text-slate-700 dark:text-slate-300' : 'font-medium text-slate-400 dark:text-slate-500',
                  )}>
                    {formatNumber(metrics.visitors)}
                  </span>
                </div>

                {metrics.hasNextStep && metrics.progressionRate !== null && metrics.progressionRate > 0 ? (
                  <>
                    <div className="mx-0.5 h-2.5 w-px bg-slate-200 dark:bg-slate-700" />
                    <div className="flex flex-1 items-center gap-1" title={`${metrics.progressedToNext.toLocaleString()} of ${metrics.visitors.toLocaleString()} progressed (${metrics.progressionRate.toFixed(1)}%)`}>
                      <ArrowRight className="h-2.5 w-2.5 flex-shrink-0 text-slate-400 dark:text-slate-500" />
                      <div className="relative h-1.5 min-w-[40px] flex-1 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                        <div
                          className={cn(
                            'absolute inset-y-0 left-0 rounded-full transition-all',
                            metrics.progressionRate >= 50 ? 'bg-emerald-500' : metrics.progressionRate >= 20 ? 'bg-blue-500' : 'bg-amber-500',
                          )}
                          style={{ width: `${Math.min(100, Math.max(2, metrics.progressionRate))}%` }}
                        />
                      </div>
                      <span className="text-[9px] font-bold tabular-nums text-slate-700 dark:text-slate-300">
                        {metrics.progressionRate.toFixed(1)}%
                      </span>
                    </div>
                  </>
                ) : metrics.hasNextStep ? (
                  <>
                    <div className="mx-0.5 h-2.5 w-px bg-slate-200 dark:bg-slate-700" />
                    <div className="flex flex-1 items-center gap-1">
                      <ArrowRight className="h-2.5 w-2.5 flex-shrink-0 text-slate-300 dark:text-slate-600" />
                      <div className="relative h-1.5 min-w-[40px] flex-1 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700" />
                      <span className="text-[9px] font-medium tabular-nums text-slate-300 dark:text-slate-600">—</span>
                    </div>
                  </>
                ) : null}

                {metrics.conversionValue > 0 && (
                  <>
                    <div className="mx-0.5 h-2.5 w-px bg-slate-200 dark:bg-slate-700" />
                    <span className="flex-shrink-0 text-[9px] font-bold tabular-nums text-emerald-600 dark:text-emerald-400" title={`Total: ${metrics.conversionValue.toLocaleString()} ${metrics.conversionValueUnit} · Avg: ${metrics.avgConversionValue.toFixed(2)} ${metrics.conversionValueUnit}`}>
                      {formatCurrency(metrics.conversionValue, metrics.conversionValueUnit)}
                    </span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* ── Split / A/B variant analytics (always visible) ── */}
          {isSplit && step.variants && step.variants.length > 0 && (
            <div className={cn(
              'border-t border-slate-100 dark:border-slate-800',
              hasAnalytics ? 'bg-slate-50/50 dark:bg-slate-900/50' : 'bg-slate-50/30 dark:bg-slate-900/30',
            )}>
              <div className="flex items-center gap-1.5 border-b border-slate-100 px-2.5 py-1 dark:border-slate-800">
                {metrics.realtimeVisitors > 0 && (
                  <div className="flex items-center gap-0.5" title={t('flowStep.activeNowTotal')}>
                    <div className="relative flex h-3 w-3 items-center justify-center">
                      <div className="absolute h-1.5 w-1.5 animate-ping rounded-full bg-green-400 opacity-75" />
                      <Zap className="relative h-2 w-2 text-green-600 dark:text-green-400" />
                    </div>
                    <span className="text-[8px] font-bold tabular-nums text-green-700 dark:text-green-300">{metrics.realtimeVisitors}</span>
                    <div className="mx-0.5 h-2 w-px bg-slate-200 dark:bg-slate-700" />
                  </div>
                )}
                <Users className={cn('h-2 w-2', hasAnalytics ? 'text-slate-400' : 'text-slate-300 dark:text-slate-600')} />
                <span className={cn(
                  'text-[8px] tabular-nums',
                  hasAnalytics ? 'font-semibold text-slate-600 dark:text-slate-400' : 'font-medium text-slate-400 dark:text-slate-500',
                )}>
                  {formatNumber(metrics.visitors)} {t('flowStep.total')}
                </span>
                {metrics.conversionValue > 0 && (
                  <span className="ml-auto text-[8px] font-bold tabular-nums text-emerald-600 dark:text-emerald-400">
                    {formatCurrency(metrics.conversionValue, metrics.conversionValueUnit)}
                  </span>
                )}
              </div>

              <div className="divide-y divide-slate-50 dark:divide-slate-800/50">
                {step.variants.slice(0, 4).map((variant, idx) => {
                  const va = variantAnalytics?.[variant.id] || stepAnalytics;
                  const vaVisitors = va?.visitors ?? 0;
                  const vaRealtime = va?.realtimeVisitors ?? 0;
                  const rate = va?.progressionRate ?? null;
                  const vaHasNext = va?.hasNextStep ?? false;

                  return (
                    <div key={variant.id} className="px-2.5 py-1">
                      <div className="mb-0.5 flex items-center gap-1">
                        <div className="flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded bg-purple-100 text-[8px] font-bold text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                          {String.fromCharCode(65 + idx)}
                        </div>
                        <span className="truncate text-[9px] font-medium text-slate-600 dark:text-slate-300">
                          {variant.label}
                        </span>
                        <span className="flex-shrink-0 text-[8px] font-semibold text-purple-500 dark:text-purple-400">
                          {step.splitType === 'geo'
                            ? (variant.regions?.length ? `${variant.regions.length} region${variant.regions.length > 1 ? 's' : ''}` : '—')
                            : `${variant.percentage}%`}
                        </span>
                        {va && va.conversionValue > 0 && (
                          <span className="ml-auto text-[8px] font-bold tabular-nums text-emerald-600 dark:text-emerald-400">
                            {formatCurrency(va.conversionValue, va.conversionValueUnit)}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-1">
                        <span className={cn(
                          'w-7 flex-shrink-0 text-right text-[8px] tabular-nums',
                          vaVisitors > 0 || vaRealtime > 0 ? 'text-slate-500 dark:text-slate-400' : 'text-slate-300 dark:text-slate-600',
                        )} title={`${vaVisitors.toLocaleString()} visitors`}>
                          {formatNumber(vaVisitors)}
                        </span>
                        {vaHasNext && rate !== null && rate > 0 ? (
                          <>
                            <div className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                              <div
                                className={cn(
                                  'absolute inset-y-0 left-0 rounded-full transition-all',
                                  rate >= 50 ? 'bg-emerald-500' : rate >= 20 ? 'bg-blue-500' : 'bg-amber-500',
                                )}
                                style={{ width: `${Math.min(100, Math.max(2, rate))}%` }}
                              />
                            </div>
                            <span className={cn(
                              'w-8 flex-shrink-0 text-right text-[8px] font-bold tabular-nums',
                              rate >= 50 ? 'text-emerald-600 dark:text-emerald-400' : rate >= 20 ? 'text-blue-600 dark:text-blue-400' : 'text-amber-600 dark:text-amber-400',
                            )}>
                              {rate.toFixed(1)}%
                            </span>
                          </>
                        ) : (
                          <>
                            <div className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700" />
                            <span className="w-8 flex-shrink-0 text-right text-[8px] font-medium text-slate-300 dark:text-slate-600">—</span>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
                {step.variants.length > 4 && (
                  <div className="px-2.5 py-1 text-center text-[8px] text-slate-400">
                    {t('flowStep.moreVariants', { count: step.variants.length - 4 })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* ── Floating Action Bar ──────────────────────────────── */}
      {(onEdit || onEditConfig || onPreview || onGetCheckoutLink || onTransformToSplit || onDelete) && (() => {
        const showEditDesign = (onEditConfig || onEdit) && !step.type.startsWith('storefront_');
        const showPreview = onPreview && hasConfigToEdit;
        const showGetLink = !!onGetCheckoutLink;
        const showOverflow = onTransformToSplit || onDuplicateDesign || onDelete;

        return (
          <div
            className={cn(
              'nodrag nopan pointer-events-none absolute -bottom-10 left-1/2 z-50 -translate-x-1/2 transition-all duration-200 group-hover/node:opacity-100',
              forceShowActionBar ? 'opacity-100' : 'opacity-0',
            )}
          >
            <div className="pointer-events-auto flex items-center gap-0.5 rounded-lg border border-slate-300 bg-white p-0.5 shadow-2xl dark:border-slate-600 dark:bg-slate-800">
              {showEditDesign && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (hasConfigToEdit && onEditConfig) {
                      onEditConfig();
                    } else if (onEdit) {
                      onEdit();
                    }
                  }}
                  className="hover:bg-primary/10 hover:text-primary flex h-8 items-center gap-1.5 rounded-md px-2.5 text-slate-600 transition-all dark:text-slate-300"
                  title={t('flowStep.editDesign')}
                >
                  <Pencil className="h-3.5 w-3.5" />
                  <span className="text-xs font-medium">{t('flowStep.editDesignLabel')}</span>
                </button>
              )}

              {showPreview && (
                <>
                  {showEditDesign && <div className="h-5 w-px bg-slate-200 dark:bg-slate-700" />}
                  <button
                    onClick={(e) => { e.stopPropagation(); onPreview!(); }}
                    className="flex h-8 items-center gap-1.5 rounded-md px-2.5 text-slate-600 transition-all hover:bg-primary/10 hover:text-primary dark:text-slate-300 dark:hover:bg-primary/10 dark:hover:text-primary"
                    title={t('flowStep.previewStep')}
                    {...(isFirstStep ? { 'data-tour': 'preview-button' } : {})}
                  >
                    <Eye className="h-3.5 w-3.5" />
                    <span className="text-xs font-medium">{t('flowStep.preview')}</span>
                  </button>
                </>
              )}

              {showGetLink && (
                <>
                  {(showEditDesign || showPreview) && <div className="h-5 w-px bg-slate-200 dark:bg-slate-700" />}
                  <button
                    onClick={(e) => { e.stopPropagation(); onGetCheckoutLink!(); }}
                    className="flex h-8 items-center gap-1.5 rounded-md px-2.5 text-emerald-600 transition-all hover:bg-emerald-50 hover:text-emerald-700 dark:text-emerald-400 dark:hover:bg-emerald-950/30 dark:hover:text-emerald-300"
                    title={t('flowStep.generateCheckoutLink')}
                  >
                    <LinkIcon className="h-3.5 w-3.5" />
                    <span className="text-xs font-medium">{t('flowStep.getLink')}</span>
                  </button>
                </>
              )}

              {showOverflow && (
                <>
                  {(showEditDesign || showPreview || showGetLink) && <div className="h-5 w-px bg-slate-200 dark:bg-slate-700" />}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        onClick={(e) => e.stopPropagation()}
                        className="flex h-8 w-8 items-center justify-center rounded-md text-slate-500 transition-all hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200"
                        title={t('flowStep.moreActions')}
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-40">
                      {onTransformToSplit && (
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onTransformToSplit(); }}>
                          <Shuffle className="mr-2 h-3.5 w-3.5" />
                          {isSplit ? t('flowStep.manageSplitTest') : t('flowStep.createSplitTest')}
                        </DropdownMenuItem>
                      )}
                      {onDuplicateDesign && hasConfigToEdit && step.config?.metadata?.editorType === 'studio' && (
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDuplicateDesign(); }}>
                          <Copy className="mr-2 h-3.5 w-3.5" />
                          {t('flowStep.duplicateDesign')}
                        </DropdownMenuItem>
                      )}
                      {onDelete && (
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDelete(); }} className="text-red-600 focus:text-red-600">
                          <Trash2 className="mr-2 h-3.5 w-3.5" />
                          {t('flowStep.removeStep')}
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              )}
            </div>
          </div>
        );
      })()}

      {/* Output Handle (right) */}
      <Handle
        type="source"
        position={Position.Right}
        id={HANDLE_IDS.RIGHT}
        className={cn(
          '!h-5 !w-5 !cursor-crosshair !rounded-full !border-2 !border-white !shadow-md !transition-all !duration-200',
          'hover:!h-6 hover:!w-6 hover:!shadow-lg',
          selected
            ? '!bg-primary hover:!ring-4 hover:!ring-primary/40'
            : '!bg-slate-400 hover:!bg-slate-500 hover:!ring-4 hover:!ring-slate-300',
        )}
        style={{ right: '-10px' }}
        isConnectable={true}
      />
    </div>
  );
}
